import React from 'react';
import { VocabularyWord, Language } from '../../types/Vocabulary';
import './VocabularyMatrix.css';

interface VocabularyDetailModalProps {
  word: VocabularyWord;
  onClose: () => void;
  onEdit: (word: VocabularyWord) => void;
  onDelete: (wordId: number) => void;
}

const LANGUAGE_LABELS: Record<Language, string> = {
  chinese: '中文',
  french: 'Français',
  hindi: 'हिन्दी',
  english: 'English'
};

const VocabularyDetailModal: React.FC<VocabularyDetailModalProps> = ({
  word,
  onClose,
  onEdit,
  onDelete
}) => {
  const handleEdit = () => {
    onEdit(word);
    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this word?')) {
      onDelete(word.id);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTranslationForLanguage = (language: Language): string | undefined => {
    return word.translations?.find(t => t.language === language)?.translation;
  };

  return (
    <div className="vocabulary-detail-modal-overlay" onClick={handleOverlayClick}>
      <div className="vocabulary-detail-modal-container">
        <div className="vocabulary-detail-modal-header">
          <h2 className="vocabulary-detail-modal-title">Word Details</h2>
          <button
            className="vocabulary-detail-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="vocabulary-detail-modal-content">
          <div className="vocabulary-detail-section">
            <div className="vocabulary-detail-word-section">
              <span className="vocabulary-detail-word">{word.word}</span>
              <span className="vocabulary-detail-language-badge">
                {LANGUAGE_LABELS[word.language]}
              </span>
            </div>
          </div>

          {word.pronunciation && (
            <div className="vocabulary-detail-section">
              <span className="vocabulary-detail-label">Pronunciation:</span>
              <span className="vocabulary-detail-pronunciation">{word.pronunciation}</span>
            </div>
          )}

          <div className="vocabulary-detail-section">
            <span className="vocabulary-detail-label">Translations:</span>
            <div className="vocabulary-detail-translations-grid">
              {(['chinese', 'french', 'hindi', 'english'] as Language[]).map((lang) => {
                if (lang === word.language) return null;
                const translation = getTranslationForLanguage(lang);
                if (!translation) return null;
                return (
                  <div key={lang} className="vocabulary-detail-translation-item">
                    <span className="vocabulary-detail-translation-lang">
                      {LANGUAGE_LABELS[lang]}:
                    </span>
                    <span className="vocabulary-detail-translation-text">{translation}</span>
                  </div>
                );
              })}
            </div>
            {(!word.translations || word.translations.length === 0) && (
              <span className="vocabulary-detail-no-translations">No translations yet</span>
            )}
          </div>

          {word.example_sentence && (
            <div className="vocabulary-detail-section">
              <span className="vocabulary-detail-label">Example:</span>
              <span className="vocabulary-detail-example">{word.example_sentence}</span>
            </div>
          )}

          {word.notes && (
            <div className="vocabulary-detail-section">
              <span className="vocabulary-detail-label">Notes:</span>
              <span className="vocabulary-detail-notes">{word.notes}</span>
            </div>
          )}
        </div>

        <div className="vocabulary-detail-modal-actions">
          <button
            className="vocabulary-detail-edit-btn"
            onClick={handleEdit}
          >
            Edit
          </button>
          <button
            className="vocabulary-detail-delete-btn"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default VocabularyDetailModal;

